"use client"

import { GroupeService } from '@repo/ui/lib/api/services/groupe.service';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

export const groupeUsageQueryKey = (id: number) => ['groupes', id, 'usage-stats'] as const;
export const groupeSubscriptionQueryKey = (id: number) => ['groupes', id, 'subscription'] as const;

export function useGroupeUsageStats(
  id: number | undefined,
  options?: UseQueryOptions<any, Error, any, ReturnType<typeof groupeUsageQueryKey>>
) {
  return useQuery({
    queryKey: groupeUsageQueryKey(id as number),
    queryFn: async () => {
      if (!id) throw new Error('groupe id required');
      const service = new GroupeService();
      return service.getUsageStats(id);
    },
    enabled: !!id,
    staleTime: 60_000,
    ...options
  });
}

export function useGroupeSubscription(
  id: number | undefined,
  options?: UseQueryOptions<any, Error, any, ReturnType<typeof groupeSubscriptionQueryKey>>
) {
  return useQuery({
    queryKey: groupeSubscriptionQueryKey(id as number),
    queryFn: async () => {
      if (!id) throw new Error('groupe id required');
      const service = new GroupeService();
      return service.getSubscription(id);
    },
    enabled: !!id,
    staleTime: 60_000,
    ...options
  });
}

export function useUpdateGroupeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: number;
      data: {
        plan_type?: string;
        subscription_status?: string;
        tier_id?: string;
        max_parcours_per_month?: number;
        current_month_parcours?: number;
      }
    }) => {
      const service = new GroupeService();
      return service.updateUsageAndExpiry(id, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['groupes', variables.id] });
      queryClient.invalidateQueries({ queryKey: groupeUsageQueryKey(variables.id) });
      queryClient.invalidateQueries({ queryKey: groupeSubscriptionQueryKey(variables.id) });
    },
  });
}


